import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ShoppingCart, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCategories } from "@/hooks/useCategories";
import { useSiteContent, scValue, useCmsItems } from "@/hooks/useSiteContent";
import { categoryName } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
    <img src="/logo-lsh-footer2.png" alt="LearnSphereHub" className="h-9 w-auto object-contain" />
  </Link>
);

const LangToggle = () => {
  const { lang, changeLang } = useI18n();
  return (
    <div className="flex items-center rounded-full border border-white/20 overflow-hidden text-xs font-semibold" data-testid="lang-toggle">
      <button data-testid="lang-it" onClick={() => changeLang("it")}
        className={`px-3 py-1.5 transition-colors ${lang === "it" ? "bg-brand-electric text-white" : "text-white/70 hover:text-white"}`}>IT</button>
      <button data-testid="lang-en" onClick={() => changeLang("en")}
        className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-brand-electric text-white" : "text-white/70 hover:text-white"}`}>EN</button>
    </div>
  );
};

const Header = () => {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { categories } = useCategories();
  const sc = useSiteContent();
  const hd = sc?.header;
  const navItems = useCmsItems("header_nav");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const hV = (base, tkey) => scValue(hd, base, lang, t(tkey));
  const catLabel = hV("nav_categories", "nav.categories");

  const fallbackLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/prodotti", label: t("nav.products") },
    { to: "/servizi-smart", label: t("nav.smart") },
    { to: "/chi-siamo", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contatti", label: t("nav.contact") },
  ];
  const navLinks = navItems.length
    ? navItems.map((it) => ({ to: it.data.url || "/", label: (lang === "en" ? it.data.label_en : it.data.label_it) || it.data.label_it || it.data.label_en || "" }))
    : fallbackLinks;
  const isExternal = (u) => /^https?:\/\//i.test(u || "");
  let splitIndex = navLinks.findIndex((l) => l.to === "/servizi-smart");
  if (splitIndex < 0) splitIndex = navLinks.length - 1;

  const NavLink = ({ l, testid, className }) => (
    isExternal(l.to)
      ? <a key={testid} href={l.to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className}>{l.label}</a>
      : <Link key={testid} to={l.to} data-testid={testid} className={className}>{l.label}</Link>
  );

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 bg-brand-navy border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.slice(0, splitIndex + 1).map((l, idx) => (
            <NavLink key={`d-${idx}`} l={l} testid={`nav-${l.to}`}
              className="px-3 py-2 text-sm font-medium text-white/80 hover:text-white transition-colors" />
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-categories" className="px-3 py-2 text-sm font-medium text-white/80 hover:text-white transition-colors inline-flex items-center gap-1">
                {catLabel} <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white">
              {categories.map((c) => (
                <DropdownMenuItem key={c.slug} data-testid={`nav-cat-${c.slug}`} onClick={() => navigate(`/categoria/${c.slug}`)}>
                  {categoryName(c, lang)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {navLinks.slice(splitIndex + 1).map((l, idx) => (
            <NavLink key={`d2-${idx}`} l={l} testid={`nav-${l.to}`}
              className="px-3 py-2 text-sm font-medium text-white/80 hover:text-white transition-colors" />
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><LangToggle /></div>

          <Link to="/carrello" data-testid="header-cart" className="relative p-2 text-white/80 hover:text-white transition-colors">
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-brand-orange text-white text-[10px] font-bold rounded-full h-4.5 min-w-[18px] px-1 flex items-center justify-center">{count}</span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-account-menu" className="p-2 text-white/80 hover:text-white transition-colors">
                  <User className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-48">
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>
                  <User className="h-4 w-4 mr-2" /> {t("nav.account")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={doLogout}>
                  <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="header-login-btn" onClick={() => navigate("/login")}
              className="hidden sm:inline-flex rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white h-9 px-4 text-sm">
              {t("nav.login")}
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="lg:hidden p-2 text-white"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-brand-navy border-white/10 text-white w-[280px] overflow-y-auto">
              <div className="mt-6 flex flex-col gap-1">
                <div className="mb-4"><LangToggle /></div>
                {navLinks.map((l, idx) => (
                  isExternal(l.to)
                    ? <a key={`m-${idx}`} href={l.to} target="_blank" rel="noopener noreferrer" data-testid={`mobile-nav-${l.to}`} onClick={() => setOpen(false)}
                        className="py-2.5 text-base font-medium text-white/85 hover:text-white border-b border-white/5">{l.label}</a>
                    : <Link key={`m-${idx}`} to={l.to} data-testid={`mobile-nav-${l.to}`} onClick={() => setOpen(false)}
                        className="py-2.5 text-base font-medium text-white/85 hover:text-white border-b border-white/5">{l.label}</Link>
                ))}
                <div className="pt-3 text-xs uppercase tracking-widest text-white/40">{catLabel}</div>
                {categories.map((c) => (
                  <Link key={c.slug} to={`/categoria/${c.slug}`} data-testid={`mobile-cat-${c.slug}`} onClick={() => setOpen(false)}
                    className="py-2 text-sm text-white/80 hover:text-white">{categoryName(c, lang)}</Link>
                ))}
                <div className="pt-4 flex flex-col gap-2">
                  {user ? (
                    <>
                      <Button data-testid="mobile-account" onClick={() => { setOpen(false); navigate("/account"); }} className="rounded-full bg-brand-electric text-white hover:bg-brand-electric/90">{t("nav.account")}</Button>
                      {user.role === "admin" && <Button data-testid="mobile-admin" onClick={() => { setOpen(false); navigate("/admin"); }} className="rounded-full bg-white/10 border border-white/25 text-white hover:bg-white/20">{t("nav.admin")}</Button>}
                      <Button data-testid="mobile-logout" onClick={() => { setOpen(false); doLogout(); }} className="rounded-full bg-white/10 border border-white/25 text-white hover:bg-white/20">{t("nav.logout")}</Button>
                    </>
                  ) : (
                    <>
                      <Button data-testid="mobile-login" onClick={() => { setOpen(false); navigate("/login"); }} className="rounded-full bg-brand-electric text-white hover:bg-brand-electric/90">{t("nav.login")}</Button>
                      <Button data-testid="mobile-register" onClick={() => { setOpen(false); navigate("/registrazione"); }} className="rounded-full bg-white/10 border border-white/25 text-white hover:bg-white/20">{t("nav.register")}</Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
